/**
 * Content for ClientFlow SEO landing pages.
 *
 * Each entry powers one route under /app/frontend/src/pages/seo/SeoLandingPage.jsx
 * and one URL listed in /app/frontend/public/sitemap.xml.
 *
 * Content principles:
 *  - One specific Norwegian search intent per page.
 *  - Natural, helpful prose written for a salon owner — no keyword stuffing.
 *  - Honest about what ClientFlow does today (no AI/SMS overpromise).
 *  - FAQs answer real questions Norwegian salon owners ask.
 */
import {
  CalendarDays,
  Users,
  Bell,
  Smartphone,
  QrCode,
  Clock,
  Phone,
  ListChecks,
  ShieldCheck,
  FileSpreadsheet,
  MessagesSquare,
  History,
  Sparkles,
  ChevronRight,
  Lock,
} from "lucide-react";

export const SEO_LANDING_PAGES = {
  "bookingsystem-frisor": {
    path: "/bookingsystem-frisor",
    title: "Bookingsystem for frisør — enkel timebestilling | ClientFlow",
    description:
      "Norsk bookingsystem for frisørsalonger. Kundene bestiller time selv, døgnet rundt, og du får full oversikt i en kalender. Prøv ClientFlow.",
    h1: "Bookingsystem for frisør",
    heroEyebrow: "For frisørsalonger",
    heroIntro:
      "Slipp telefonjogging og glemte avtaler. Med ClientFlow får kundene dine en egen bookingside hvor de velger tjeneste, dato og tid selv — og du får alt samlet i én kalender.",
    features: [
      {
        icon: CalendarDays,
        title: "Online timebestilling",
        text: "Kundene booker selv, døgnet rundt. Du slipper å svare på telefon eller DM når du er midt i en klipp.",
      },
      {
        icon: Smartphone,
        title: "Fungerer på mobil",
        text: "Hele bookingflyten er bygget mobil-først. Kundene booker like enkelt fra telefonen som fra PC.",
      },
      {
        icon: QrCode,
        title: "QR-kode til salongen",
        text: "Skriv ut en QR-kode kundene kan scanne i salongen for å booke neste time før de går.",
      },
      {
        icon: Clock,
        title: "Åpningstider og ferie",
        text: "Sett åpningstider per ukedag og marker stengte dager (ferie, kurs, helligdager). Kundene kan ikke booke når du har stengt.",
      },
    ],
    faqs: [
      {
        q: "Trenger kundene en konto for å booke?",
        a: "Nei. Kundene oppgir bare navn og e-post eller telefonnummer. Ingen registrering eller passord.",
      },
      {
        q: "Kan flere ansatte bruke samme system?",
        a: "Ja. På Pro-pakken kan du legge til flere brukere og se en samlet teamkalender. Hver ansatt får sin egen pålogging.",
      },
      {
        q: "Hva skjer hvis to kunder prøver å bestille samme tid?",
        a: "Systemet hindrer dobbeltbooking automatisk. Den som bekrefter først får tiden — den andre ser den som opptatt med en gang.",
      },
      {
        q: "Hva koster bookingsystemet?",
        a: "Basic koster 199 kr/mnd, Pro 399 kr/mnd og Premium 599 kr/mnd (alle eks. mva). Det finnes også en gratis demo du kan prøve uten forpliktelser.",
      },
    ],
    ctaTitle: "Klar for et enklere bookingsystem i salongen?",
    ctaText:
      "Book en kort gjennomgang så viser vi hvordan ClientFlow kan passe akkurat din salong.",
  },

  "crm-for-salong": {
    path: "/crm-for-salong",
    title: "CRM for salong — kundekartotek og oppfølging | ClientFlow",
    description:
      "Enkel CRM for salong og servicebedrift. Hold orden på kunder, notater, historikk og oppfølging på ett sted. Bygget for norske småbedrifter.",
    h1: "CRM for salong",
    heroEyebrow: "Kundekartotek",
    heroIntro:
      "Et godt kunderegister er den enkleste måten å få flere faste kunder på. ClientFlow gir deg et oversiktlig CRM hvor du ser hvem som har vært innom, hva de booker oftest, og hvem som trenger en påminnelse.",
    features: [
      {
        icon: Users,
        title: "Alle kundene på ett sted",
        text: "Navn, telefon, e-post og notater samlet i ett bilde. Søk frem en kunde på sekunder.",
      },
      {
        icon: History,
        title: "Historikk og besøk",
        text: "Se alle tidligere bookinger per kunde — når de var innom sist og hvilken tjeneste de valgte.",
      },
      {
        icon: ListChecks,
        title: "Kunder å følge opp",
        text: "ClientFlow viser deg automatisk hvilke faste kunder som ikke har vært innom på en stund, så du kan ta kontakt.",
      },
      {
        icon: MessagesSquare,
        title: "Notater per kunde",
        text: "Skriv ned hva kunden liker — fargenummer, allergier, preferanser — så er det klart neste gang.",
      },
    ],
    faqs: [
      {
        q: "Er CRM-et inkludert i alle pakkene?",
        a: "Ja. Et grunnleggende kundekartotek er inkludert fra Basic. Pro og Premium gir deg flere muligheter som notater, historikk og kunder å følge opp.",
      },
      {
        q: "Kan jeg importere kundene mine fra et gammelt system?",
        a: "Ja. På Premium hjelper vi deg med import av kundelisten din slik at du slipper å taste alt på nytt.",
      },
      {
        q: "Hvordan håndteres GDPR og personvern?",
        a: "ClientFlow er bygget for å være enkelt å bruke innenfor GDPR. Du er behandlingsansvarlig for kundene dine, og kan slette opplysninger når som helst. Se personvernerklæringen for detaljer.",
      },
      {
        q: "Kan jeg eksportere kundene mine?",
        a: "Ja. Du kan eksportere både kundeliste og bookinger til CSV når som helst, slik at dataen alltid er din.",
      },
    ],
    ctaTitle: "Få oversikt over kundene dine",
    ctaText:
      "Book demo, så ser vi sammen hvordan CRM-et i ClientFlow kan hjelpe akkurat din salong.",
  },

  "timebestilling-frisor": {
    path: "/timebestilling-frisor",
    title: "Timebestilling for frisør — online booking 24/7 | ClientFlow",
    description:
      "Online timebestilling for frisør og barbersalong. Kundene bestiller time selv på minuttet, og du får full kontroll på kalenderen i ClientFlow.",
    h1: "Timebestilling for frisør",
    heroEyebrow: "Online timebestilling",
    heroIntro:
      "De fleste kundene vil booke time uten å ringe. Med ClientFlow får frisørsalongen din en egen bookingside med dine tjenester, dine priser og dine åpningstider — kundene tar resten selv.",
    features: [
      {
        icon: CalendarDays,
        title: "Døgnåpen booking",
        text: "Kundene kan bestille time klokken 22 en søndag — like greit som tirsdag morgen. Du ser alt i kalenderen din.",
      },
      {
        icon: Phone,
        title: "Færre telefoner i salongen",
        text: "Når kundene booker selv på nett, slipper du å avbryte en klipp for å ta telefonen.",
      },
      {
        icon: Bell,
        title: "Påminnelser før time",
        text: "På Premium sender ClientFlow automatisk e-postpåminnelse 24 timer før timen — slik at færre kunder glemmer.",
      },
      {
        icon: ChevronRight,
        title: "Avbestillingslenke",
        text: "Kundene får en lenke for å avbestille hvis noe kommer i veien. Da frigjør tiden seg automatisk for nye kunder.",
      },
    ],
    faqs: [
      {
        q: "Hvor lang tid tar det å komme i gang?",
        a: "De fleste salonger er oppe og kjører samme dag. Du legger inn tjenester, priser og åpningstider, og kan dele bookinglenken med en gang.",
      },
      {
        q: "Kan jeg fortsatt ta bookinger på telefon?",
        a: "Ja. Du legger inn telefonbookinger manuelt i kalenderen — de havner i samme oversikt som de som kommer fra nett.",
      },
      {
        q: "Får kundene en bekreftelse?",
        a: "Ja. Etter booking får kunden en bekreftelse på e-post med tidspunkt og en lenke for å avbestille om nødvendig.",
      },
      {
        q: "Kan jeg sperre dager jeg har ferie?",
        a: "Ja. Du markerer ferie, kurs eller helligdager i kalenderen, og kundene kan ikke booke disse dagene.",
      },
    ],
    ctaTitle: "Få din egen bookingside",
    ctaText:
      "Book demo så viser vi deg hvordan timebestillingen kan se ut for din salong.",
  },

  "kunderegister-salong": {
    path: "/kunderegister-salong",
    title: "Kunderegister for salong — enkel kundeoversikt | ClientFlow",
    description:
      "Digitalt kunderegister for salong, frisør og behandler. Hold orden på kunder, notater og historikk på norsk. Trygt og GDPR-vennlig.",
    h1: "Kunderegister for salong",
    heroEyebrow: "Digitalt kunderegister",
    heroIntro:
      "Papirkalender og post-it-lapper er greit en stund — men når kundelisten vokser, går detaljene tapt. Med ClientFlow får salongen din et ryddig digitalt kunderegister hvor alt ligger trygt på ett sted.",
    features: [
      {
        icon: Users,
        title: "Søkbar kundeliste",
        text: "Finn kunden på under et sekund. Søk på navn, telefon eller e-post.",
      },
      {
        icon: MessagesSquare,
        title: "Notater som følger med",
        text: "Skriv ned alt fra hårfarge til allergier. Notatene er der neste gang kunden booker.",
      },
      {
        icon: FileSpreadsheet,
        title: "Eksport når du vil",
        text: "Last ned hele kundelisten som CSV-fil. Du eier dataen din — alltid.",
      },
      {
        icon: ShieldCheck,
        title: "Trygt og GDPR-vennlig",
        text: "Data lagres kryptert. Du kan slette eller eksportere kundeopplysninger når som helst.",
      },
    ],
    faqs: [
      {
        q: "Hvor mange kunder kan jeg legge inn?",
        a: "Det er ingen øvre grense — verken på Basic, Pro eller Premium. Bygg listen så stor som du trenger.",
      },
      {
        q: "Kan jeg flytte over kundene fra et Excel-ark?",
        a: "Ja. På Premium hjelper vi deg med å importere kundene dine fra Excel eller et annet system.",
      },
      {
        q: "Hva skjer hvis en kunde ber meg slette dataen sin?",
        a: "Du kan slette kunden direkte fra kunderegisteret. Bookingene henger igjen som anonymiserte tall i statistikken, slik at oversikten din ikke endres.",
      },
      {
        q: "Hvem har tilgang til kundene mine?",
        a: "Kun du og de ansatte du selv inviterer. ClientFlow selger aldri data og deler ikke kundene dine med tredjeparter.",
      },
    ],
    ctaTitle: "Få et ryddig kunderegister",
    ctaText:
      "Book en kort gjennomgang så viser vi deg hvordan kunderegisteret fungerer i praksis.",
  },

  "alternativ-til-timma": {
    path: "/alternativ-til-timma",
    title: "Sammenlign bookingsystemer — alternativ til Timma | ClientFlow",
    description:
      "Vurderer du flere bookingsystemer for salongen? ClientFlow er et norsk alternativ med booking, CRM og timebestilling i ett enkelt system. Ærlig pris fra 199 kr/mnd.",
    h1: "Sammenlign ClientFlow med andre bookingsystemer",
    heroEyebrow: "Sammenligning",
    heroIntro:
      "Vurderer du Timma eller andre bookingsystemer? ClientFlow er et norsk alternativ for små salonger og servicebedrifter som ønsker booking, CRM, timebestilling og kundeoppfølging i ett enkelt system — på norsk, til en forutsigbar pris.",
    features: [
      {
        icon: Sparkles,
        title: "Bygget for norske småbedrifter",
        text: "Norske priser i kroner, norsk språk i hele løsningen, og norsk support — utviklet i samarbeid med små salonger her hjemme.",
      },
      {
        icon: ListChecks,
        title: "Forutsigbar prising uten bindingstid",
        text: "Tre pakker fra 199 kr/mnd til 599 kr/mnd eks. mva. Du kan si opp når du vil. Ingen skjulte gebyrer.",
      },
      {
        icon: Lock,
        title: "Dataen din er din",
        text: "Eksporter både kunder og bookinger til CSV når du vil. Vi binder ikke dataen din til oss.",
      },
      {
        icon: MessagesSquare,
        title: "Personlig support",
        text: "Du snakker med folk som faktisk har bygget systemet — ikke en chatbot på engelsk.",
      },
    ],
    faqs: [
      {
        q: "Hva tilbyr ClientFlow?",
        a: "ClientFlow er et norsk bookingsystem og CRM laget for små servicebedrifter. Du får online timebestilling, en oversiktlig kalender, kundekartotek med historikk og notater, og verktøy for å følge opp faste kunder — alt i ett verktøy på norsk.",
      },
      {
        q: "Kan jeg flytte kundene mine fra et annet system til ClientFlow?",
        a: "Ja. På Premium-pakken hjelper vi deg med å importere kundelisten fra et annet bookingsystem, så du slipper å taste alt på nytt.",
      },
      {
        q: "Er det bindingstid?",
        a: "Nei. Du betaler per måned og kan si opp når du vil — uten oppsigelsestid eller skjulte gebyrer.",
      },
      {
        q: "Kan jeg prøve før jeg bestemmer meg?",
        a: "Ja. Det finnes en gratis demo du kan teste på en fiktiv bedrift, og vi kan også åpne en personlig demo-konto til deg etter en kort prat.",
      },
    ],
    ctaTitle: "Vurderer du å bytte bookingsystem?",
    ctaText:
      "Book en kort gjennomgang så ser vi sammen om ClientFlow passer for salongen din.",
  },

  "norsk-bookingsystem": {
    path: "/norsk-bookingsystem",
    title: "Norsk bookingsystem for små bedrifter | ClientFlow",
    description:
      "ClientFlow er et norsk bookingsystem laget for små salonger, behandlere og servicebedrifter. Online booking, kundekartotek og kalender — alt på norsk.",
    h1: "Norsk bookingsystem",
    heroEyebrow: "Laget i Norge",
    heroIntro:
      "Et bookingsystem trenger ikke være komplisert eller dyrt. ClientFlow er et norsk bookingsystem laget for små servicebedrifter — salonger, behandlere, terapeuter og enkeltpersonforetak — med norsk språk, norsk støtte og ærlig prising i kroner.",
    features: [
      {
        icon: CalendarDays,
        title: "Online booking",
        text: "Egen bookingside og kalender på norsk. Kundene bestiller selv, du får full oversikt.",
      },
      {
        icon: Users,
        title: "Kunderegister",
        text: "Hold orden på kunder, historikk og notater. Søkbart og enkelt å bruke.",
      },
      {
        icon: Bell,
        title: "Påminnelser før time",
        text: "Automatiske e-postpåminnelser før timen reduserer no-shows. Inkludert i Premium.",
      },
      {
        icon: ShieldCheck,
        title: "Trygt og GDPR-vennlig",
        text: "Data lagres innenfor EU/EØS. Passord er hashet. Du kan eksportere eller slette dataen din når du vil.",
      },
    ],
    faqs: [
      {
        q: "Hvem passer ClientFlow for?",
        a: "Frisører, neglesalonger, hudpleiere, terapeuter, behandlere og andre små servicebedrifter i Norge som vil ha online booking og enkelt kundekartotek i ett verktøy.",
      },
      {
        q: "Er alt på norsk?",
        a: "Ja. Brukergrensesnitt, bookingside, e-poster og support er på norsk.",
      },
      {
        q: "Hva koster det?",
        a: "Tre pakker: Basic 199 kr/mnd, Pro 399 kr/mnd og Premium 599 kr/mnd — alle eks. mva. Det er ingen bindingstid og du kan starte med en gratis demo.",
      },
      {
        q: "Hvor lagres dataen?",
        a: "Hos en europeisk skyleverandør, innenfor EU/EØS. Vi tilstreber alltid kort vei mellom deg, dataen din og oss.",
      },
    ],
    ctaTitle: "Prøv et norsk bookingsystem",
    ctaText:
      "Book demo så viser vi deg hvordan ClientFlow passer din lille bedrift.",
  },
};
